import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../../Utils/persistHelpers"
import { logout } from "../../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../../User/redux/user.actions"
import { REQUESTS_GIFTS_SLICE_NAME } from "./requestsGifts.constants"
import { getRequestsGifts } from "./requestsGifts.thunks"

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "created_timestamp", label: "Created date", active: true },
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "amount", label: "Sum", active: true },
  { field_id: "admin_name", label: "Admin name", active: true }
]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    created_timestamp: null,
    user_full_name: [],
    user_email: [],
    amount: { min: null, max: null, type: null }
  },

  visibleFiltersAdmin: { user_email: true, user_full_name: true }
}

const slice = createSlice({
  name: REQUESTS_GIFTS_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `requestsGiftsSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `requestsGiftsSetProp({ key, value })`,
    // matching the `requestsAutoRefundSetProp`/`adminUsersSetProp` slices.
    requestsGiftsSetProp(state, action) {
      const { key, value } = action.payload
      if (["filters", "visibleFilters", "visibleFiltersAdmin"].includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearRequestsGiftsFilters(state) {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- list ---------------- */
      .addCase(getRequestsGifts.pending, (state) => {
        state.fetching = true
      })
      .addCase(getRequestsGifts.fulfilled, (state, action) => {
        const result = action.payload
        state.fetching = false
        if (result?.results) {
          state.result = result
        } else {
          state.result = {
            count: result?.length || 0,
            results: result
          }
        }
      })
      .addCase(getRequestsGifts.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, (state) => ({
        ...initialState,
        filters: state.filters,
        columnsAdmin: state.columnsAdmin,
        visibleFiltersAdmin: state.visibleFiltersAdmin
      }))

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["requests-gifts"]?.data
        if (!preference || preference.version !== REQUESTS_GIFTS_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const requestsGiftsActions = slice.actions

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const REQUESTS_GIFTS_PREFERENCES_VERSION = 1

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.requestsGifts.filters, initialState.filters)

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "requestsGifts",
    storage,
    // Only the user's own table setup survives a reload — the requests list is re-fetched.
    whitelist: ["filters", "columnsAdmin", "visibleFiltersAdmin"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
